import type { Plan } from '@/fixtures/plans';
import type { Permission, Role } from '@/types/auth';

/**
 * GrowthBook user attributes derived from auth state.
 *
 * These keys MUST match the attribute names registered in GrowthBook cloud and
 * the SSR setup in `@/lib/growthbook`. A mismatch silently breaks all role/plan
 * targeting — the client previously set `roles` (plural) while every condition
 * and the SSR path use `role`, so targeting only resolved during SSR. Routing
 * both call sites through this typed shape means a key typo is a compile error,
 * not a silent production bug.
 */
export interface GrowthBookAuthAttributes {
  permissions: Permission[];
  role: Role[];
  subscriptionType: Plan['name'];
}

// This helper exists to give the GrowthBook auth attribute keys a single typed
// source of truth — that contract is what stops the `roles`/`role` drift, not
// the three lines of mapping.
export function buildAuthAttributes(
  roles: Iterable<Role>,
  permissions: Iterable<Permission>,
  subscriptionType: Plan['name']
): GrowthBookAuthAttributes {
  return {
    role: Array.from(roles),
    permissions: Array.from(permissions),
    subscriptionType,
  };
}
